import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, MoreVertical } from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from '../../ui/select';
import type { HeaderTab, HeaderStat, HeaderAction, StatusBadgeConfig, ViewMode } from '../../../contexts/DynamicHeaderContext';

export const TAB_THRESHOLD = 5;

// ===================== GmailUnreadBadge =====================
export function GmailUnreadBadge() {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let unsub: (() => void) | undefined;
    import('../../../lib/stores/gmailStore').then(({ useGmailStore }) => {
      setCount(useGmailStore.getState().unreadCount);
      unsub = useGmailStore.subscribe((s) => setCount(s.unreadCount));
    });
    return () => unsub?.();
  }, []);
  if (count <= 0) return null;
  return (
    <div
      className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-[16px] px-1 text-white text-[9px] font-semibold rounded-full"
      style={{ backgroundColor: '#EA4335' }}
    >
      {count > 99 ? '99+' : count}
    </div>
  );
}

// ===================== OutlookUnreadBadge =====================
export function OutlookUnreadBadge() {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let unsub: (() => void) | undefined;
    import('../../../lib/stores/outlookStore').then(({ useOutlookStore }) => {
      setCount(useOutlookStore.getState().unreadCount);
      unsub = useOutlookStore.subscribe((s) => setCount(s.unreadCount));
    });
    return () => unsub?.();
  }, []);
  if (count <= 0) return null;
  return (
    <div
      className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-[16px] px-1 text-white text-[9px] font-semibold rounded-full"
      style={{ backgroundColor: '#0078D4' }}
    >
      {count > 99 ? '99+' : count}
    </div>
  );
}

// ===================== TabsDropdown =====================
interface TabsDropdownProps {
  tabs: HeaderTab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
}

export function TabsDropdown({ tabs, activeTab, onTabChange }: TabsDropdownProps) {
  const activeTabData = tabs.find(t => t.id === activeTab) || tabs[0];
  return (
    <div className="hidden md:block">
      <Select value={activeTab} onValueChange={onTabChange}>
        <SelectTrigger className="h-8 px-2.5 text-xs bg-black/5 dark:bg-white/5 border-0 rounded-md min-w-[140px] max-w-[200px]">
          <div className="flex items-center gap-1.5 truncate">
            {activeTabData?.icon && (
              <span className="[&>svg]:w-3.5 [&>svg]:h-3.5 flex-shrink-0">{activeTabData.icon}</span>
            )}
            <span className="font-medium truncate">{activeTabData?.label}</span>
            {activeTabData?.badge !== undefined && activeTabData.badge > 0 && (
              <span className="ml-0.5 px-1 py-0.5 text-[10px] rounded-full bg-red-500 text-white flex-shrink-0">
                {activeTabData.badge}
              </span>
            )}
          </div>
        </SelectTrigger>
        <SelectContent className="min-w-[200px] max-h-[400px]">
          {tabs.map(tab => (
            <SelectItem key={tab.id} value={tab.id} className="py-2">
              <div className="flex items-center gap-2">
                {tab.icon && <span className="[&>svg]:w-4 [&>svg]:h-4">{tab.icon}</span>}
                <span>{tab.label}</span>
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] rounded-full bg-red-500 text-white">
                    {tab.badge}
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ===================== StatusBadge =====================
interface StatusBadgeProps {
  config: StatusBadgeConfig;
}

export function StatusBadge({ config }: StatusBadgeProps) {
  const currentOption = config.options.find(o => o.value === config.value);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative flex items-center" ref={ref}>
      <button
        onClick={() => !config.disabled && setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1.5 px-2 py-px rounded text-xs font-medium leading-none ${currentOption?.color || 'bg-gray-100 dark:bg-gray-700'} flex-shrink-0 ${
          config.disabled ? 'opacity-60 cursor-not-allowed' : 'hover:ring-1 hover:ring-gray-300 dark:hover:ring-gray-600 cursor-pointer'
        }`}
      >
        {currentOption?.label || config.value}
        {!config.disabled && <ChevronDown className="w-3 h-3 opacity-50" />}
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-[9999] bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[120px]">
          {config.options.map(option => (
            <button
              key={option.value}
              onClick={() => { config.onChange(option.value); setIsOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 ${config.value === option.value ? 'bg-gray-50 dark:bg-gray-700/50' : ''}`}
            >
              <span className={`inline-block px-1.5 py-0.5 rounded ${option.color}`}>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================== StatBadge =====================
interface StatBadgeProps {
  stat: HeaderStat;
}

export function StatBadge({ stat }: StatBadgeProps) {
  const colorClasses: Record<string, string> = {
    green: 'text-green-600 dark:text-green-400',
    red: 'text-red-600 dark:text-red-400',
    orange: 'text-orange-600 dark:text-orange-400',
    blue: 'text-blue-600 dark:text-blue-400',
    default: 'text-foreground'
  };
  const textColorClass = colorClasses[stat.color || 'default'];

  if (stat.progressBar) {
    return (
      <div className="flex items-center gap-1.5">
        {stat.icon && <span className={`${textColorClass} [&>svg]:w-3 [&>svg]:h-3`}>{stat.icon}</span>}
        <div className="w-12 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full bg-green-500 transition-all duration-300" style={{ width: `${stat.progressValue || 0}%` }} />
        </div>
        <span className={`text-xs font-medium ${textColorClass}`}>{stat.value}</span>
      </div>
    );
  }

  const content = (
    <div className="flex items-center gap-1 text-xs" title={stat.tooltip}>
      {stat.icon && <span className={`${textColorClass} [&>svg]:w-3 [&>svg]:h-3`}>{stat.icon}</span>}
      <span className={`font-medium ${textColorClass}`}>{stat.value}</span>
    </div>
  );

  if (stat.onClick) {
    return <button onClick={stat.onClick} className="hover:opacity-80 transition-opacity" title={stat.tooltip}>{content}</button>;
  }
  return content;
}

// ===================== ViewModeToggle =====================
interface ViewModeToggleProps {
  modes: ViewMode[];
  current?: string;
  onChange?: (mode: string) => void;
}

export function ViewModeToggle({ modes, current, onChange }: ViewModeToggleProps) {
  return (
    <div className="inline-flex items-center gap-0.5 bg-black/5 dark:bg-white/5 rounded-md p-0.5">
      {modes.map((mode) => (
        <button
          key={mode.id}
          onClick={() => onChange?.(mode.id)}
          className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-all whitespace-nowrap ${
            current === mode.id
              ? 'bg-white dark:bg-gray-800 text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-white/10'
          }`}
          title={mode.label || mode.id}
        >
          <span className="[&>svg]:w-3.5 [&>svg]:h-3.5">{mode.icon}</span>
          {/* Con tante viste (calendario: 6) le etichette schiacciavano titolo e schede: solo icone fino a 2xl */}
          {mode.label && <span className={modes.length > 4 ? 'hidden 2xl:inline' : 'hidden lg:inline'}>{mode.label}</span>}
        </button>
      ))}
    </div>
  );
}

// ===================== ActionsMenu =====================
interface ActionsMenuProps {
  actions: HeaderAction[];
}

export function ActionsMenu({ actions }: ActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.right - 200 });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleClick}
        className="inline-flex items-center justify-center w-7 h-7 border border-white/20 rounded-md text-muted-foreground hover:bg-accent transition-colors"
        title="Altre azioni"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-48 rounded-lg shadow-xl bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-white/10 z-[20050] py-1"
          style={{ top: position.top, left: position.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {actions.map(action => (
            <button
              key={action.id}
              onClick={() => { if (action.disabled) return; action.onClick(); setIsOpen(false); }}
              disabled={action.disabled}
              title={action.title}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                action.disabled
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : action.variant === 'danger'
                  ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/10'
              }`}
            >
              {action.icon && <span className="[&>svg]:w-4 [&>svg]:h-4">{action.icon}</span>}
              <span className="text-left">{action.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
